import React, { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, orderBy, where, getDocs, doc, getDoc, limit } from 'firebase/firestore';
import { auth, db } from './firebaseConfig';
import { Dialog, DialogBackdrop, DialogPanel, TransitionChild } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/20/solid';
import { Cog6ToothIcon, PhoneIcon, AtSymbolIcon, ChatBubbleLeftRightIcon, PhoneArrowUpRightIcon, ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import { GiDominoMask, GiRadarSweep, GiSoundWaves, GiEchoRipples, GiSpeedometer } from "react-icons/gi";
import { MdContacts } from "react-icons/md";
import { ShieldCheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import usePageTitle from './utils/usePageTitle';

// Function to get navigation items
function getFilteredNavigation(pathname) {
  return [
    { name: 'Home', href: '/overview', icon: GiSpeedometer, current: pathname === '/overview' },
    { name: 'Relay', href: '/dashboard', icon: GiDominoMask, current: pathname === '/dashboard' },
    { name: 'Messages', href: '/dashboard/trace', icon: GiEchoRipples, current: pathname === '/dashboard/trace' },
    { name: 'World Call', href: '/world-call', icon: GiSoundWaves, current: pathname === '/world-call' },
    { name: 'Contacts', href: '/contacts', icon: MdContacts, current: pathname === '/contacts' },
    { name: 'Monitor', href: '/dashboard/monitor', icon: GiRadarSweep, current: pathname === '/dashboard/monitor' },
  ];
}

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Overview() {
  usePageTitle('Overview');

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingStates, setLoadingStates] = useState({
    aliases: true,
    phoneAliases: true,
    monitoring: true,
    calls: true,
    messages: true
  });
  const [stats, setStats] = useState({
    activeAliases: 0,
    inactiveAliases: 0,
    activePhoneAliases: 0,
    inactivePhoneAliases: 0,
    breachesDetected: 0,
    lastCalledNumbers: [],
    recentMessages: []
  });

  const location = useLocation();
  const navigate = useNavigate();
  const navigation = getFilteredNavigation(location.pathname);
  const settingsNavItem = { name: 'Settings', href: '/settings', icon: Cog6ToothIcon, current: location.pathname === '/settings' };

  // Load user statistics
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadUserStats(currentUser.uid);
      } else {
        setUser(null);
        // If no user, set all loading states to false to show empty states
        setLoadingStates({
          aliases: false,
          phoneAliases: false,
          monitoring: false,
          calls: false,
          messages: false
        });
      }
    });
    
    return () => unsubscribe();
  }, []);

  // Add TriplePixel purchase confirmation event if user came from checkout page
  useEffect(() => {
    // Check if user came from checkout page using referrer or sessionStorage
    const referrer = document.referrer;
    const cameFromCheckout = referrer.includes('/checkout') || sessionStorage.getItem('cameFromCheckout');
    
    // Only fire the event if the user came from checkout and not just reloading the overview
    if (cameFromCheckout && !sessionStorage.getItem('purchaseEventFired')) {
      // Load the first.js script
      const script = document.createElement('script');
      script.src = 'https://api.config-security.com/first.js?host=www.chatodyssey.com&plat=stripe';
      document.head.appendChild(script);
      
      // Mark that we've fired the purchase event to avoid duplicate events
      sessionStorage.setItem('purchaseEventFired', 'true');
      // Clear the checkout flag
      sessionStorage.removeItem('cameFromCheckout');
    }
  }, []);

  const loadUserStats = async (uid) => {
    // Load user aliases
    loadUserAliases(uid);
    
    // Load phone aliases  
    loadPhoneAliases(uid);
    
    // Load call history
    loadCallHistory(uid);
    
    // Load recent messages
    loadRecentMessages(uid);
    
    // Load monitoring data
    loadMonitoringData(uid);
  };

  const loadUserAliases = async (uid) => {
    try {
      const userAliasDoc = await getDoc(doc(db, 'userAliases', uid));
      let activeAliases = 0;
      let inactiveAliases = 0;

      if (userAliasDoc.exists()) {
        const userData = userAliasDoc.data();
        if (userData.aliases) {
          userData.aliases.forEach(alias => {
            if (alias.status === 'Active') {
              activeAliases++;
            } else {
              inactiveAliases++;
            }
          });
        }
      }

      setStats(prev => ({
        ...prev,
        activeAliases,
        inactiveAliases
      }));
      
      setLoadingStates(prev => ({ ...prev, aliases: false }));
    } catch (error) {
      console.error('Error loading user aliases:', error);
      setLoadingStates(prev => ({ ...prev, aliases: false }));
    }
  };

  const loadPhoneAliases = async (uid) => {
    try {
      const phoneAliasesSnapshot = await getDocs(collection(db, 'userAliases', uid, 'phoneAliases'));
      let activePhoneAliases = 0;
      let inactivePhoneAliases = 0;

      phoneAliasesSnapshot.forEach(doc => {
        const phoneData = doc.data();
        if (phoneData.connectionStatus === 'Active') {
          activePhoneAliases++;
        } else {
          inactivePhoneAliases++;
        }
      });

      setStats(prev => ({
        ...prev,
        activePhoneAliases,
        inactivePhoneAliases
      }));
      
      setLoadingStates(prev => ({ ...prev, phoneAliases: false }));
    } catch (error) {
      console.error('Error loading phone aliases:', error);
      setLoadingStates(prev => ({ ...prev, phoneAliases: false }));
    }
  };

  const loadCallHistory = async (uid) => {
    if (!uid) {
      setLoadingStates(prev => ({ ...prev, calls: false }));
      return;
    }
    
    try {
      const callsRef = collection(db, 'userCallHistory', uid, 'calls');
      const callsQuery = query(callsRef, orderBy('startTime', 'desc'), limit(5));
      const callsSnapshot = await getDocs(callsQuery);
      
      const lastCalledNumbers = callsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          number: data.destinationNumber,
          timestamp: data.startTime ? data.startTime.toDate() : new Date(),
          duration: data.duration || 'Unknown'
        };
      });

      setStats(prev => ({
        ...prev,
        lastCalledNumbers
      }));
      
      setLoadingStates(prev => ({ ...prev, calls: false }));
    } catch (error) {
      console.error('Error loading call history:', error);
      setLoadingStates(prev => ({ ...prev, calls: false }));
    }
  };

  const loadMonitoringData = async (uid) => {
    try {
      let breachesDetected = 0;
      const identifiersQuery = query(collection(db, 'userMonitor', uid, 'identifiers'));
      const identifiersSnap = await getDocs(identifiersQuery);
      
      identifiersSnap.docs.forEach(doc => {
        const data = doc.data();
        if (data.hasBreaches) {
          breachesDetected++;
        }
      });

      setStats(prev => ({
        ...prev,
        breachesDetected
      }));
      
      setLoadingStates(prev => ({ ...prev, monitoring: false }));
    } catch (error) {
      console.log('Breach monitoring not available for user');
      setLoadingStates(prev => ({ ...prev, monitoring: false }));
    }
  };

  const loadRecentMessages = async (uid) => {
    try {
      // Query the messagingplatform collection for conversations
      const conversationsRef = collection(db, `messagingplatform/${uid}/conversations`);
      const conversationsQuery = query(conversationsRef, orderBy('lastTimestamp', 'desc'), limit(10));
      const conversationsSnapshot = await getDocs(conversationsQuery);
      
      const recentMessages = [];

             // Get the most recent message from each conversation
       for (const convoDoc of conversationsSnapshot.docs) {
         if (recentMessages.length >= 3) break; // Limit to 3 recent messages
        
        const convoData = convoDoc.data();
        const conversationId = convoDoc.id;
        
        try {
          // Query the most recent message from this conversation
          const messagesRef = collection(db, `messagingplatform/${uid}/conversations/${conversationId}/messages`);
          const messagesQuery = query(messagesRef, orderBy('timestamp', 'desc'), limit(1));
          const messagesSnapshot = await getDocs(messagesQuery);
          
          if (!messagesSnapshot.empty) {
            const messageDoc = messagesSnapshot.docs[0];
            const messageData = messageDoc.data();
            
            // Handle timestamp properly
            let messageTimestamp = new Date();
            if (messageData.timestamp) {
              if (messageData.timestamp.toDate) {
                messageTimestamp = messageData.timestamp.toDate();
              } else if (messageData.timestamp instanceof Date) {
                messageTimestamp = messageData.timestamp;
              } else if (typeof messageData.timestamp === 'string') {
                messageTimestamp = new Date(messageData.timestamp);
              }
            }
            
            recentMessages.push({
              aliasNumber: convoData.platformNumber,
              contactNumber: convoData.otherParty,
              message: messageData.body || messageData.text || 'Message',
              timestamp: messageTimestamp,
              direction: messageData.direction,
              aliasLabel: convoData.platformNumber // You could enhance this with actual labels if needed
            });
          }
        } catch (error) {
          console.log(`No messages found for conversation ${conversationId}`);
        }
      }

      // Sort by timestamp and get latest 3
      const sortedMessages = recentMessages
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 3);

      setStats(prev => ({
        ...prev,
        recentMessages: sortedMessages
      }));
      
      setLoadingStates(prev => ({ ...prev, messages: false }));

    } catch (error) {
      console.error('Error loading recent messages:', error);
      setLoadingStates(prev => ({ ...prev, messages: false }));
    }
  };

  const handleCallNumberClick = (number) => {
    // Store the number in localStorage so World Call can pick it up
    localStorage.setItem('pendingCallNumber', number);
    navigate('/world-call');
  };

  // Create skeleton loading components
  const SkeletonCard = ({ className = "" }) => (
    <div className={`bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 p-6 animate-pulse ${className}`}
         style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'}}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-x-3">
          <div className="w-8 h-8 bg-white/10 rounded-lg"></div>
          <div className="h-6 bg-white/10 rounded w-32"></div>
        </div>
        <div className="w-16 h-4 bg-white/10 rounded"></div>
      </div>
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-4 bg-white/10 rounded w-full"></div>
        ))}
      </div>
    </div>
  );

  const StatsSkeleton = () => (
    <div className="bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 p-6 animate-pulse"
         style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'}}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-x-3">
          <div className="w-8 h-8 bg-white/10 rounded-lg"></div>
          <div className="h-6 bg-white/10 rounded w-32"></div>
        </div>
        <div className="w-16 h-4 bg-white/10 rounded"></div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="text-center p-4 bg-white/5 rounded-xl">
            <div className="h-8 bg-white/10 rounded w-12 mx-auto mb-2"></div>
            <div className="h-4 bg-white/10 rounded w-20 mx-auto"></div>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <>
        {/* Mobile sidebar - Same as main component but simplified */}
        <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
          <DialogBackdrop transition className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0" />
          <div className="fixed inset-0 flex">
            <DialogPanel transition className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full">
              <div 
                className="flex grow flex-col gap-y-5 overflow-y-auto m-4 rounded-2xl border border-white/10 shadow-2xl"
                style={{
                  background: 'rgba(0, 0, 0, 0.2)',
                  backdropFilter: 'blur(20px)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
                }}
              >
                <div className="flex h-16 shrink-0 items-center justify-center px-4">
                  <img alt="Relay" src="/images/logowhite.png" className="h-8 w-auto" />
                </div>
                <div className="px-4 space-y-3">
                  {[1, 2, 3, 4, 5].map(i => (
                    <div key={i} className="h-12 bg-white/10 rounded-lg animate-pulse"></div>
                  ))}
                </div>
              </div>
            </DialogPanel>
          </div>
        </Dialog>

        {/* Desktop sidebar - Same structure */}
        <div className={`hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-all duration-300 ${
          sidebarExpanded ? 'lg:w-72' : 'lg:w-20'
        }`}>
          <div className="relative">
            <div 
              className="flex grow flex-col gap-y-5 overflow-y-auto m-4 rounded-2xl border border-white/10 shadow-2xl relative"
              style={{
                background: 'rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(20px)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)',
                height: 'calc(100vh - 2rem)'
              }}
            >
                             <div className={`flex h-16 shrink-0 items-center justify-center transition-all duration-300 ${sidebarExpanded ? 'px-4' : 'px-1'}`}>
                 <img
                   alt="Relay"
                   src="/images/logowhite.png"
                   className={`transition-all duration-300 ${sidebarExpanded ? 'h-8 w-auto' : 'h-10 w-auto max-w-none'}`}
                   style={{ filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.1))' }}
                 />
               </div>
                             <div className={`space-y-3 flex-1 ${sidebarExpanded ? 'px-4' : 'px-1'}`}>
                 {[1, 2, 3, 4, 5].map(i => (
                   <div key={i} className="h-12 bg-white/10 rounded-lg animate-pulse"></div>
                 ))}
               </div>
            </div>
            <div 
              className="absolute top-0 right-0 w-4 h-full cursor-pointer group flex items-center justify-center"
              onClick={() => setSidebarExpanded(!sidebarExpanded)}
              style={{ zIndex: 60 }}
            >
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white/10 backdrop-blur-md rounded-full p-1 border border-white/20">
                {sidebarExpanded ? (
                  <ChevronLeftIcon className="h-4 w-4 text-white/70" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4 text-white/70" />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main content with skeleton loading */}
        <div 
          className={`flex-1 min-h-screen transition-all duration-300 ${
            sidebarExpanded ? 'lg:pl-80' : 'lg:pl-28'
          }`} 
          style={{background: 'linear-gradient(to bottom right, #0d0520, #1a0b2e, #0a0615, #050208)'}}
        >
          <button 
            type="button" 
            onClick={() => setSidebarOpen(true)} 
            className="fixed top-4 left-4 z-50 lg:hidden bg-white/10 backdrop-blur-md rounded-full p-3 text-white hover:bg-white/20 transition-all border border-white/20"
            style={{
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'
            }}
          >
            <Bars3Icon aria-hidden="true" className="h-6 w-6" />
          </button>

          <div className="px-4 py-6 lg:px-8">
            {/* Header skeleton */}
            <div className="mb-8">
              <div className="text-center lg:text-left">
                <div className="h-10 bg-white/10 rounded w-64 mb-2 animate-pulse"></div>
                <div className="h-6 bg-white/10 rounded w-96 animate-pulse"></div>
              </div>
            </div>

            {/* Quick Actions skeleton */}
            <div className="mb-8">
              <div className="h-8 bg-white/10 rounded w-32 mb-6 animate-pulse"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 p-6 animate-pulse"
                       style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)', minHeight: '140px'}}>
                    <div className="w-12 h-12 bg-white/10 rounded-xl mb-4"></div>
                    <div className="h-6 bg-white/10 rounded w-32 mb-2"></div>
                    <div className="h-4 bg-white/10 rounded w-24"></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Statistics Grid skeleton */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <StatsSkeleton />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Mobile sidebar - Liquid Glass */}
      <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
        <DialogBackdrop transition className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0" />
        <div className="fixed inset-0 flex">
          <DialogPanel transition className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full">
            <TransitionChild>
              <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                <button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                  <span className="sr-only">Close sidebar</span>
                  <XMarkIcon aria-hidden="true" className="h-6 w-6 text-white" />
                </button>
              </div>
            </TransitionChild>
            
            <div 
              className="flex grow flex-col gap-y-5 overflow-y-auto m-4 rounded-2xl border border-white/10 shadow-2xl"
              style={{
                background: 'rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(20px)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
              }}
            >
              <div className="flex h-16 shrink-0 items-center justify-center px-4">
                <img
                  alt="Relay"
                  src="/images/logowhite.png"
                  className="h-8 w-auto"
                />
              </div>
              <nav className="flex flex-1 flex-col px-4">
                <ul role="list" className="flex flex-1 flex-col gap-y-7">
                  <li>
                    <ul role="list" className="space-y-3">
                      {navigation.map((item) => (
                        <li key={item.name}>
                          <Link
                            to={item.href}
                            className="group flex items-center gap-x-3 p-3 text-sm font-medium transition-all duration-200"
                            onClick={() => setSidebarOpen(false)}
                          >
                            <item.icon 
                              aria-hidden="true" 
                              className={classNames(
                                "h-6 w-6 shrink-0 transition-colors duration-200",
                                item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                              )} 
                            />
                            <span className={classNames(
                              "transition-colors duration-200",
                              item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                            )}>
                              {item.name}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                  <li className="mt-auto mb-4">
                    <Link
                      to={settingsNavItem.href}
                      className="group flex items-center gap-x-3 p-3 text-sm font-medium transition-all duration-200"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <settingsNavItem.icon 
                        aria-hidden="true" 
                        className={classNames(
                          "h-6 w-6 shrink-0 transition-colors duration-200",
                          settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                        )} 
                      />
                      <span className={classNames(
                        "transition-colors duration-200",
                        settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                      )}>
                        {settingsNavItem.name}
                      </span>
                    </Link>
                  </li>
                </ul>
              </nav>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Desktop sidebar - Liquid Glass Collapsible */}
      <div className={`hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-all duration-300 ${
        sidebarExpanded ? 'lg:w-72' : 'lg:w-20'
      }`}>
        <div className="relative">
          <div 
            className="flex grow flex-col gap-y-5 overflow-y-auto m-4 rounded-2xl border border-white/10 shadow-2xl relative"
            style={{
              background: 'rgba(0, 0, 0, 0.2)',
              backdropFilter: 'blur(20px)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)',
              height: 'calc(100vh - 2rem)'
            }}
          >
            {/* Header with logo */}
            <div className={`flex h-16 shrink-0 items-center justify-center transition-all duration-300 ${sidebarExpanded ? 'px-4' : 'px-1'}`}>
              <img
                alt="Relay"
                src="/images/logowhite.png"
                className={`transition-all duration-300 ${sidebarExpanded ? 'h-8 w-auto' : 'h-10 w-auto max-w-none'}`}
                style={{ filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.1))' }}
              />
            </div>

            <nav className={`flex flex-1 flex-col ${sidebarExpanded ? 'px-4' : 'px-1'}`}>
              <ul role="list" className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul role="list" className="space-y-3">
                    {navigation.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          className={classNames(
                            'group flex items-center text-sm font-medium transition-all duration-200 rounded-lg',
                            sidebarExpanded ? 'gap-x-3 p-3' : 'justify-center p-2'
                          )}
                          title={!sidebarExpanded ? item.name : undefined}
                        >
                          <item.icon 
                            aria-hidden="true" 
                            className={classNames(
                              "h-6 w-6 shrink-0 transition-colors duration-200",
                              item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                            )} 
                          />
                          {sidebarExpanded && (
                            <span className={classNames(
                              "transition-opacity duration-300",
                              item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                            )}>
                              {item.name}
                            </span>
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>
                <li className="mt-auto mb-4">
                                      <Link
                      to={settingsNavItem.href}
                      className={classNames(
                        'group flex items-center text-sm font-medium transition-all duration-200 rounded-lg',
                        sidebarExpanded ? 'gap-x-3 p-3' : 'justify-center p-2'
                      )}
                      title={!sidebarExpanded ? settingsNavItem.name : undefined}
                    >
                      <settingsNavItem.icon 
                        aria-hidden="true" 
                        className={classNames(
                          "h-6 w-6 shrink-0 transition-colors duration-200",
                          settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                        )} 
                      />
                      {sidebarExpanded && (
                        <span className={classNames(
                          "transition-opacity duration-300",
                          settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                        )}>
                          {settingsNavItem.name}
                        </span>
                      )}
                    </Link>
                </li>
              </ul>
            </nav>
          </div>

          {/* Right border expand/collapse area */}
          <div 
            className="absolute top-0 right-0 w-4 h-full cursor-pointer group flex items-center justify-center"
            onClick={() => setSidebarExpanded(!sidebarExpanded)}
            style={{ zIndex: 60 }}
          >
            {/* Hover indicator */}
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white/10 backdrop-blur-md rounded-full p-1 border border-white/20">
              {sidebarExpanded ? (
                <ChevronLeftIcon className="h-4 w-4 text-white/70" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 text-white/70" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div 
        className={`flex-1 min-h-screen transition-all duration-300 ${
          sidebarExpanded ? 'lg:pl-80' : 'lg:pl-28'
        }`} 
        style={{background: 'linear-gradient(to bottom right, #0d0520, #1a0b2e, #0a0615, #050208)'}}
      >
        {/* Mobile Menu Button */}
        <button 
          type="button" 
          onClick={() => setSidebarOpen(true)} 
          className="fixed top-4 left-4 z-50 lg:hidden bg-white/10 backdrop-blur-md rounded-full p-3 text-white hover:bg-white/20 transition-all border border-white/20"
          style={{
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'
          }}
        >
          <span className="sr-only">Open sidebar</span>
          <Bars3Icon aria-hidden="true" className="h-6 w-6" />
        </button>

        {/* Page content */}
        <div className="px-4 py-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="text-center lg:text-left">
              <h1 className="text-4xl font-light text-white mb-2">
                Welcome back
              </h1>
              <p className="text-white/60 text-lg">Here's what's happening with your virtual identities</p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mb-8">
            <h2 className="text-2xl font-light text-white mb-6">Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              
              {/* Create Phone Number */}
              <Link to="/dashboard?mode=phone&action=create" className="group h-full">
                <div className="bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 p-6 hover:bg-black/30 transition-all duration-300 hover:scale-105 h-full flex flex-col"
                     style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)', minHeight: '140px'}}>
                  <div className="flex items-center justify-center w-12 h-12 bg-blue-500/20 rounded-xl mb-4">
                    <PhoneIcon className="h-6 w-6 text-blue-400" />
                  </div>
                  <h3 className="text-white font-medium mb-2">Create Phone Number</h3>
                  <p className="text-white/60 text-sm flex-1">Get a new phone relay number</p>
                </div>
              </Link>

              {/* Create Email Alias */}
              <Link to="/dashboard?mode=email" className="group h-full">
                <div className="bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 p-6 hover:bg-black/30 transition-all duration-300 hover:scale-105 h-full flex flex-col"
                     style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)', minHeight: '140px'}}>
                  <div className="flex items-center justify-center w-12 h-12 bg-green-500/20 rounded-xl mb-4">
                    <AtSymbolIcon className="h-6 w-6 text-green-400" />
                  </div>
                  <h3 className="text-white font-medium mb-2">Create Email Alias</h3>
                  <p className="text-white/60 text-sm flex-1">Generate a new email relay</p>
                </div>
              </Link>

              {/* Make Phone Call */}
              <Link to="/world-call" className="group h-full">
                <div className="bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 p-6 hover:bg-black/30 transition-all duration-300 hover:scale-105 h-full flex flex-col"
                     style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)', minHeight: '140px'}}>
                  <div className="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-xl mb-4">
                    <PhoneArrowUpRightIcon className="h-6 w-6 text-purple-400" />
                  </div>
                  <h3 className="text-white font-medium mb-2">Make Phone Call</h3>
                  <p className="text-white/60 text-sm flex-1">International calling worldwide</p>
                </div>
              </Link>

              {/* Send/Receive Texts */}
              <Link to="/dashboard/trace" className="group h-full">
                <div className="bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 p-6 hover:bg-black/30 transition-all duration-300 hover:scale-105 h-full flex flex-col"
                     style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)', minHeight: '140px'}}>
                  <div className="flex items-center justify-center w-12 h-12 bg-orange-500/20 rounded-xl mb-4">
                    <ChatBubbleLeftRightIcon className="h-6 w-6 text-orange-400" />
                  </div>
                  <h3 className="text-white font-medium mb-2">Send & Receive Texts</h3>
                  <p className="text-white/60 text-sm flex-1">Manage your conversations</p>
                </div>
              </Link>

            </div>
          </div>

          {/* Statistics Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Alias Statistics */}
            <div className="bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 p-6"
                 style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'}}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-x-3">
                  <div className="flex items-center justify-center w-8 h-8 bg-blue-500/20 rounded-lg">
                    <GiDominoMask className="h-5 w-5 text-blue-400" />
                  </div>
                  <h3 className="text-xl font-light text-white">Relay Statistics</h3>
                </div>
                <Link to="/dashboard" className="text-blue-400 hover:text-blue-300 text-sm">View All</Link>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-white/5 rounded-xl">
                  {loadingStates.aliases ? (
                    <div className="h-8 bg-white/10 rounded w-12 mx-auto mb-2 animate-pulse"></div>
                  ) : (
                    <div className="text-2xl font-light text-green-400 mb-1">{stats.activeAliases}</div>
                  )}
                  <div className="text-white/60 text-sm">Active Email Aliases</div>
                </div>
                <div className="text-center p-4 bg-white/5 rounded-xl">
                  {loadingStates.aliases ? (
                    <div className="h-8 bg-white/10 rounded w-12 mx-auto mb-2 animate-pulse"></div>
                  ) : (
                    <div className="text-2xl font-light text-gray-400 mb-1">{stats.inactiveAliases}</div>
                  )}
                  <div className="text-white/60 text-sm">Inactive Email Aliases</div>
                </div>
                <div className="text-center p-4 bg-white/5 rounded-xl">
                  {loadingStates.phoneAliases ? (
                    <div className="h-8 bg-white/10 rounded w-12 mx-auto mb-2 animate-pulse"></div>
                  ) : (
                    <div className="text-2xl font-light text-green-400 mb-1">{stats.activePhoneAliases}</div>
                  )}
                  <div className="text-white/60 text-sm">Active Phone Numbers</div>
                </div>
                <div className="text-center p-4 bg-white/5 rounded-xl">
                  {loadingStates.phoneAliases ? (
                    <div className="h-8 bg-white/10 rounded w-12 mx-auto mb-2 animate-pulse"></div>
                  ) : (
                    <div className="text-2xl font-light text-gray-400 mb-1">{stats.inactivePhoneAliases}</div>
                  )}
                  <div className="text-white/60 text-sm">Inactive Phone Numbers</div>
                </div>
              </div>
            </div>

            {/* Monitoring Statistics */}
            <div className="bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 p-6"
                 style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'}}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-x-3">
                  <div className="flex items-center justify-center w-8 h-8 bg-green-500/20 rounded-lg">
                    <GiRadarSweep className="h-5 w-5 text-green-400" />
                  </div>
                  <h3 className="text-xl font-light text-white">Security Monitoring</h3>
                </div>
                <Link to="/dashboard/monitor" className="text-blue-400 hover:text-blue-300 text-sm">View Details</Link>
              </div>
              
              <div className="flex items-center justify-center p-8">
                {loadingStates.monitoring ? (
                  <div className="text-center">
                    <div className="w-16 h-16 bg-white/10 rounded-2xl mb-4 mx-auto animate-pulse"></div>
                    <div className="h-10 bg-white/10 rounded w-16 mx-auto mb-2 animate-pulse"></div>
                    <div className="h-4 bg-white/10 rounded w-32 mx-auto animate-pulse"></div>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="flex items-center justify-center w-16 h-16 bg-green-500/20 rounded-2xl mb-4 mx-auto">
                      {stats.breachesDetected > 0 ? (
                        <ExclamationTriangleIcon className="h-8 w-8 text-red-400" />
                      ) : (
                        <ShieldCheckIcon className="h-8 w-8 text-green-400" />
                      )}
                    </div>
                    <div className={`text-3xl font-light mb-2 ${stats.breachesDetected > 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {stats.breachesDetected}
                    </div>
                    <div className="text-white/60 text-sm">
                      {stats.breachesDetected === 0 ? 'No Breaches Detected' : 
                       stats.breachesDetected === 1 ? 'Breach Detected' : 'Breaches Detected'}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Last Called Numbers */}
            <div className="bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 p-6"
                 style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'}}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-x-3">
                  <div className="flex items-center justify-center w-8 h-8 bg-purple-500/20 rounded-lg">
                    <GiSoundWaves className="h-5 w-5 text-purple-400" />
                  </div>
                  <h3 className="text-xl font-light text-white">Recent Calls</h3>
                </div>
                <Link to="/world-call" className="text-blue-400 hover:text-blue-300 text-sm">Make Call</Link>
              </div>
              
              <div className="space-y-3">
                {loadingStates.calls ? (
                  [1, 2, 3].map(i => (
                    <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded-xl animate-pulse">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-white/10 rounded-lg"></div>
                        <div>
                          <div className="h-4 bg-white/10 rounded w-24 mb-1"></div>
                          <div className="h-3 bg-white/10 rounded w-16"></div>
                        </div>
                      </div>
                      <div className="h-4 bg-white/10 rounded w-12"></div>
                    </div>
                  ))
                ) : stats.lastCalledNumbers.length > 0 ? (
                  stats.lastCalledNumbers.map((call, index) => (
                    <div 
                      key={index}
                      onClick={() => handleCallNumberClick(call.number)}
                      className="flex items-center justify-between p-3 bg-white/5 rounded-xl hover:bg-white/10 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                          <PhoneIcon className="h-4 w-4 text-blue-400" />
                        </div>
                        <div>
                          <div className="text-white font-medium">{call.number}</div>
                          <div className="text-white/60 text-xs">
                            {new Date(call.timestamp).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <div className="text-white/60 text-sm">{call.duration}</div>
                    </div>
                  ))
                ) : (
                  <div className="text-center p-6 text-white/60">
                    <PhoneIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No recent calls</p>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Messages */}
            <div className="bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 p-6"
                 style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'}}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-x-3">
                  <div className="flex items-center justify-center w-8 h-8 bg-orange-500/20 rounded-lg">
                    <GiEchoRipples className="h-5 w-5 text-orange-400" />
                  </div>
                  <h3 className="text-xl font-light text-white">Recent Messages</h3>
                </div>
                <Link to="/dashboard/trace" className="text-blue-400 hover:text-blue-300 text-sm">View All</Link>
              </div>
              
              <div className="space-y-3">
                {loadingStates.messages ? (
                  [1, 2, 3].map(i => (
                    <div key={i} className="p-3 bg-white/5 rounded-xl animate-pulse">
                      <div className="flex items-center justify-between mb-2">
                        <div className="h-4 bg-white/10 rounded w-24"></div>
                        <div className="h-3 bg-white/10 rounded w-4"></div>
                      </div>
                      <div className="h-4 bg-white/10 rounded w-full mb-1"></div>
                      <div className="h-3 bg-white/10 rounded w-32"></div>
                    </div>
                  ))
                ) : stats.recentMessages.length > 0 ? (
                  stats.recentMessages.map((message, index) => (
                    <div key={index} className="p-3 bg-white/5 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-white font-medium text-sm">{message.contactNumber}</div>
                        <div className="text-white/60 text-xs">
                          {message.direction === 'inbound' ? '↓' : '↑'}
                        </div>
                      </div>
                      <div className="text-white/80 text-sm mb-1 line-clamp-2">
                        {message.message}
                      </div>
                      <div className="text-white/60 text-xs">
                        via {message.aliasLabel} • {message.timestamp.toLocaleDateString()}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center p-6 text-white/60">
                    <ChatBubbleLeftRightIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No recent messages</p>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
} 